import { httpClient } from './httpClient';
import {
  Event,
  EventType,
  EventsListFilters,
  CreateEventData,
  UpdateEventData,
} from '@/types/events';

/**
 * Events Service
 * Handles all API calls to /api/v2/events and /api/v2/event-types
 */
class EventsService {
  private static instance: EventsService;
  private readonly eventsBaseUrl = '/api/v2/events';
  private readonly typesBaseUrl = '/api/v2/event-types';

  static getInstance(): EventsService {
    if (!EventsService.instance) {
      EventsService.instance = new EventsService();
    }
    return EventsService.instance;
  }

  /**
   * Get list of events with optional filters
   */
  async getEvents(filters?: EventsListFilters): Promise<Event[]> {
    try {
      const params: Record<string, string | number> = {};

      if (filters?.account_id) {
        params.account_id = filters.account_id;
      }
      if (filters?.status) {
        params.status = filters.status;
      }
      if (filters?.is_active !== undefined) {
        params.is_active = filters.is_active.toString();
      }
      if (filters?.search) {
        params.search = filters.search;
      }

      const response = await httpClient.get<{ data: Event[] }>(
        this.eventsBaseUrl,
        { params }
      );

      // API format from EVENTS_API_GUIDE.md: { data: [...] }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }

      return [];
    } catch (error: any) {
      // Se é 404, retorna array vazio (endpoint não existe ainda ou sem dados)
      if (error?.response?.status === 404) {
        return [];
      }
      console.error('[EventsService] Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Get a specific event by ID
   */
  async getEvent(id: number): Promise<Event> {
    try {
      const response = await httpClient.get<{ data: Event }>(
        `${this.eventsBaseUrl}/${id}`
      );

      // API format from EVENTS_API_GUIDE.md: { data: {...} }
      if (response?.data) {
        return response.data;
      }

      throw new Error('Failed to fetch event');
    } catch (error) {
      console.error(`[EventsService] Error fetching event ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all event types
   */
  async getEventTypes(): Promise<EventType[]> {
    try {
      const response = await httpClient.get<{ data: EventType[] }>(this.typesBaseUrl);

      // API format from EVENTS_API_GUIDE.md: { data: [...] }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }

      return [];
    } catch (error: any) {
      // Se é 404, retorna array vazio (endpoint não existe ainda)
      if (error?.response?.status === 404) {
        return [];
      }
      console.error('[EventsService] Error fetching event types:', error);
      throw error;
    }
  }

  /**
   * Create a new event
   */
  async createEvent(data: CreateEventData): Promise<Event> {
    try {
      const response = await httpClient.post<{ message: string; data: Event }>(
        this.eventsBaseUrl,
        data
      );

      // API format from EVENTS_API_GUIDE.md: { message: "...", data: {...} }
      if (response?.data) {
        return response.data;
      }

      throw new Error('Failed to create event');
    } catch (error) {
      console.error('[EventsService] Error creating event:', error);
      throw error;
    }
  }

  /**
   * Update an existing event
   */
  async updateEvent(id: number, data: UpdateEventData): Promise<Event> {
    try {
      const response = await httpClient.put<{ message: string; data: Event }>(
        `${this.eventsBaseUrl}/${id}`,
        data
      );

      // API format from EVENTS_API_GUIDE.md: { message: "...", data: {...} }
      if (response?.data) {
        return response.data;
      }

      throw new Error('Failed to update event');
    } catch (error) {
      console.error(`[EventsService] Error updating event ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete an event
   */
  async deleteEvent(id: number): Promise<void> {
    try {
      await httpClient.delete(`${this.eventsBaseUrl}/${id}`);
      // API format from EVENTS_API_GUIDE.md: { message: "Evento removido com sucesso." }
    } catch (error) {
      console.error(`[EventsService] Error deleting event ${id}:`, error);
      throw error;
    }
  }
}

export const eventsService = EventsService.getInstance();
